import React, { useEffect, useMemo, useRef, useState } from "react";
import NoNotification from "../../../assets/images/NoNotification.jpg";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../../screens/DashBoard/DashboardService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";

export type NotificationItem = {
  _id: string;
  userId?: string;
  title?: string;
  body?: string;
  text?: string;
  status?: string;
  createdAt?: string;
  type?: string;
};

type TabKey = "all" | "leads" | "tasks" | "system";

type PrefKey = "stageChange" | "assignment" | "reminder" | "teamAlert";

type NotificationPrefs = Record<PrefKey, boolean>;

const DEFAULT_PREFS: NotificationPrefs = {
  stageChange: true,
  assignment: true,
  reminder: true,
  teamAlert: true,
};

const PREF_LABELS: { key: PrefKey; label: string; hint: string }[] = [
  {
    key: "stageChange",
    label: "Stage changes",
    hint: "Won, lost, and pipeline stage updates",
  },
  {
    key: "assignment",
    label: "Assignments",
    hint: "Lead assigned or reassigned to you",
  },
  {
    key: "reminder",
    label: "Reminders",
    hint: "Follow-up reminders and overdue alerts",
  },
  {
    key: "teamAlert",
    label: "Team alerts",
    hint: "Team activity and member updates",
  },
];

type NotificationPanelProps = {
  open: boolean;
  onClose: () => void;
  notifications: NotificationItem[];
  unreadCount: number;
  onMarkRead: (notification: NotificationItem) => void | Promise<void>;
  onMarkAllRead: () => void | Promise<void>;
  onViewAll?: () => void;
  ignoreRef?: React.RefObject<HTMLElement | null>;
};

const stripHtml = (html?: string) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const formatRelativeTime = (createdAt?: string) => {
  if (!createdAt) return "";
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return "";
  const diffInMinutes = Math.floor((Date.now() - created) / (1000 * 60));
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
};

const inferType = (n: NotificationItem): TabKey => {
  const raw = `${n.type || ""} ${n.title || ""} ${n.text || ""} ${n.body || ""}`.toLowerCase();
  if (raw.includes("lead")) return "leads";
  if (raw.includes("task") || raw.includes("reminder") || raw.includes("follow"))
    return "tasks";
  if (raw.includes("system") || raw.includes("security") || raw.includes("password"))
    return "system";
  return "system";
};

const CategoryIcon = ({ type }: { type: TabKey }) => {
  if (type === "leads") {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F9F0F4] text-[#A02448]">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      </span>
    );
  }
  if (type === "tasks") {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-[#3B82F6]">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280]">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 01-3.46 0" />
      </svg>
    </span>
  );
};

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "leads", label: "Leads" },
  { key: "tasks", label: "Tasks" },
  { key: "system", label: "System" },
];

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onClose,
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onViewAll,
  ignoreRef,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [showAll, setShowAll] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowAll(false);
      setMenuOpenId(null);
      setShowSettings(false);
      return;
    }
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (ignoreRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onClose, ignoreRef]);

  const loadPrefs = async () => {
    setPrefsLoading(true);
    try {
      const res = await getNotificationPreferences();
      const loaded = res?.data?.prefs || res?.prefs || {};
      setPrefs({ ...DEFAULT_PREFS, ...loaded });
    } catch {
      setPrefs(DEFAULT_PREFS);
    } finally {
      setPrefsLoading(false);
    }
  };

  const openSettings = () => {
    setShowSettings(true);
    loadPrefs();
  };

  const savePrefs = async () => {
    setPrefsSaving(true);
    try {
      await updateNotificationPreferences(prefs);
      openSuccessNotification("Notification preferences saved");
      setShowSettings(false);
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save preferences");
    } finally {
      setPrefsSaving(false);
    }
  };

  const categorized = useMemo(
    () =>
      (notifications || []).map((n) => ({
        ...n,
        _category: inferType(n),
      })),
    [notifications]
  );

  const tabCounts = useMemo(() => {
    const counts: Record<TabKey, number> = {
      all: categorized.filter((n) => n.status === "unread").length,
      leads: categorized.filter(
        (n) => n._category === "leads" && n.status === "unread"
      ).length,
      tasks: categorized.filter(
        (n) => n._category === "tasks" && n.status === "unread"
      ).length,
      system: categorized.filter(
        (n) => n._category === "system" && n.status === "unread"
      ).length,
    };
    return counts;
  }, [categorized]);

  const filtered = useMemo(() => {
    const list =
      activeTab === "all"
        ? categorized
        : categorized.filter((n) => n._category === activeTab);
    return showAll ? list : list.slice(0, 6);
  }, [categorized, activeTab, showAll]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-[calc(100%+10px)] z-[1600] flex w-[min(420px,calc(100vw-24px))] max-h-[min(640px,calc(100vh-90px))] flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_12px_40px_rgba(15,23,42,0.14)]"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 pb-2 pt-4">
        <h3 className="text-lg font-bold tracking-tight text-[#111827]">
          {showSettings ? "Notification settings" : "Notifications"}
        </h3>
        <div className="flex items-center gap-2">
          {!showSettings && unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => onMarkAllRead()}
              className="text-sm font-semibold text-[#A02448] transition hover:text-[#4338CA]"
            >
              Mark all as read
            </button>
          ) : null}
          {showSettings ? (
            <button
              type="button"
              className="text-sm font-semibold text-[#A02448]"
              onClick={() => setShowSettings(false)}
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#E5E7EB] text-[#6B7280] transition hover:bg-[#F8FAFC]"
              aria-label="Notification settings"
              onClick={openSettings}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {showSettings ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 [scrollbar-width:thin]">
          {prefsLoading ? (
            <p className="py-8 text-center text-sm text-[#9CA3AF]">Loading…</p>
          ) : (
            <ul className="space-y-3">
              {PREF_LABELS.map((item) => (
                <li
                  key={item.key}
                  className="flex items-start justify-between gap-3 rounded-xl border border-[#EEF0F5] px-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#111827]">
                      {item.label}
                    </p>
                    <p className="mt-0.5 text-xs text-[#6B7280]">{item.hint}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prefs[item.key]}
                    onClick={() =>
                      setPrefs((p) => ({ ...p, [item.key]: !p[item.key] }))
                    }
                    className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                      prefs[item.key] ? "bg-[#841C39]" : "bg-[#E5E7EB]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
                        prefs[item.key] ? "left-[22px]" : "left-0.5"
                      }`}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex justify-end gap-2 pb-2">
            <button
              type="button"
              onClick={() => setShowSettings(false)}
              className="rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm font-semibold text-[#4D5369]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={prefsSaving || prefsLoading}
              onClick={savePrefs}
              className="rounded-xl bg-[#841C39] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {prefsSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="shrink-0 border-b border-[#EEF0F5] px-3 pb-3">
            <div className="flex gap-1">
              {TABS.map((tab) => {
                const active = activeTab === tab.key;
                const count = tabCounts[tab.key];
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.key);
                      setShowAll(false);
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "bg-[#F9F0F4] text-[#A02448]"
                        : "text-[#6B7280] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    {tab.label}
                    {count > 0 ? (
                      <span
                        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                          active
                            ? "bg-[#A02448] text-white"
                            : "bg-[#E5E7EB] text-[#4B5563]"
                        }`}
                      >
                        {count > 99 ? "99+" : count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-10">
                <img
                  src={NoNotification}
                  alt="No notifications"
                  className="h-40 w-40 object-contain opacity-90"
                />
                <p className="mt-2 text-sm text-[#9CA3AF]">No notifications yet</p>
              </div>
            ) : (
              <ul>
                {filtered.map((notification, index) => {
                  const unread = notification.status === "unread";
                  const title =
                    stripHtml(notification.title) ||
                    stripHtml(notification.text) ||
                    "Notification";
                  const description =
                    stripHtml(notification.body) ||
                    (stripHtml(notification.text) !== title
                      ? stripHtml(notification.text)
                      : "");
                  const category = notification._category as TabKey;

                  return (
                    <li
                      key={notification._id || index}
                      className={`relative border-b border-[#F3F4F6] ${
                        unread ? "bg-[#F8F7FF]" : "bg-white"
                      } hover:bg-[#F9FAFB]`}
                    >
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 px-4 py-3.5 text-left"
                        onClick={() => onMarkRead(notification)}
                      >
                        {unread ? (
                          <span className="mt-4 h-2 w-2 shrink-0 rounded-full bg-[#A02448]" />
                        ) : (
                          <span className="mt-4 h-2 w-2 shrink-0" />
                        )}
                        <CategoryIcon type={category} />
                        <span className="min-w-0 flex-1 pr-6">
                          <span className="block text-sm font-semibold text-[#111827]">
                            {title}
                          </span>
                          {description ? (
                            <span className="mt-0.5 line-clamp-2 block text-sm text-[#6B7280]">
                              {description}
                            </span>
                          ) : null}
                          <span className="mt-1.5 block text-xs text-[#9CA3AF]">
                            {formatRelativeTime(notification.createdAt)}
                          </span>
                        </span>
                      </button>

                      <div className="absolute right-2 top-3">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#6B7280]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(
                              menuOpenId === notification._id
                                ? null
                                : notification._id
                            );
                          }}
                          aria-label="More actions"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5" />
                            <circle cx="12" cy="12" r="1.5" />
                            <circle cx="12" cy="19" r="1.5" />
                          </svg>
                        </button>
                        {menuOpenId === notification._id ? (
                          <div className="absolute right-0 top-9 z-10 w-40 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-lg">
                            <button
                              type="button"
                              className="block w-full px-3 py-2 text-left text-sm text-[#374151] hover:bg-[#F8FAFC]"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMarkRead(notification);
                                setMenuOpenId(null);
                              }}
                            >
                              Mark as read
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="shrink-0 border-t border-[#EEF0F5] px-4 py-3">
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#A02448] transition hover:text-[#4338CA]"
              onClick={() => {
                if (onViewAll) onViewAll();
                else setShowAll(true);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
              </svg>
              View all notifications
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationPanel;
